import { z } from 'zod/v4';

import { assertSuccessfulResponseGenericErrorCode } from '../http/assertSuccessfulResponseGenericErrorCode';
import { endpoint } from '../http/endpoint';
import { AssemblyIdRuntype } from '../idTypes';
import { getNormalizedFileName } from './exportFileName';

export const exportEndpoints = {
    'GET /export/costed-bom-xlsx': endpoint({
        description: 'Returns an excel file with all of the costed BOM',
        pathParams: z.undefined(),
        queryParams: z.object({
            rfq: z.string(),
            template_id: z.string().optional(),
        }),
        requestBody: z.undefined(),
        invalidates: ['GET /rfqs/:rfqId/snapshot'],
        responseBody: z.object({ fileName: z.string(), blob: z.instanceof(Blob) }),
        handleResponse: async (response, endpoint) => {
            await assertSuccessfulResponseGenericErrorCode(response, endpoint);
            return { fileName: getNormalizedFileName(response), blob: await response.blob() };
        },
    }),

    'GET /export/bom-xlsx': endpoint({
        description: 'Returns an excel file with all of the BOM items in a given assembly',
        pathParams: z.undefined(),
        queryParams: z.object({ assemblyId: AssemblyIdRuntype, templateId: z.string().optional() }),
        requestBody: z.undefined(),
        responseBody: z.object({ fileName: z.string(), blob: z.instanceof(Blob) }),
        handleResponse: async (response, endpoint) => {
            await assertSuccessfulResponseGenericErrorCode(response, endpoint);
            return { fileName: getNormalizedFileName(response), blob: await response.blob() };
        },
    }),
    'GET /export/assembly-bom-xlsx': endpoint({
        description:
            'Same as /export/bom-xlsx but when assembly is imported via API and has no rfq (project) associated with it',
        pathParams: z.undefined(),
        queryParams: z.object({ assemblyId: AssemblyIdRuntype, templateId: z.string().optional() }),
        requestBody: z.undefined(),
        responseBody: z.object({ fileName: z.string(), blob: z.instanceof(Blob) }),
        handleResponse: async (response, endpoint) => {
            await assertSuccessfulResponseGenericErrorCode(response, endpoint);
            return { fileName: getNormalizedFileName(response), blob: await response.blob() };
        },
    }),
    'GET /export/calculation-cost-xlsx': endpoint({
        description: 'Returns an excel file with all of the calculation cost items in a given assembly',
        pathParams: z.undefined(),
        queryParams: z.object({ calculation_assembly_details_id: z.string() }),
        requestBody: z.undefined(),
        responseBody: z.object({ fileName: z.string(), blob: z.instanceof(Blob) }),
        handleResponse: async (response, endpoint) => {
            await assertSuccessfulResponseGenericErrorCode(response, endpoint);
            return { fileName: getNormalizedFileName(response), blob: await response.blob() };
        },
    }),

    'GET /export/manufacturing-xlsx': endpoint({
        description: 'Returns an excel file with all of the manufacturing items in a given assembly',
        pathParams: z.undefined(),
        queryParams: z.object({ manufacturing_assembly_details_id: z.string() }),
        requestBody: z.undefined(),
        responseBody: z.object({ fileName: z.string(), blob: z.instanceof(Blob) }),
        handleResponse: async (response, endpoint) => {
            await assertSuccessfulResponseGenericErrorCode(response, endpoint);
            return { fileName: getNormalizedFileName(response), blob: await response.blob() };
        },
    }),

    'GET /rfqs/:rfqId/snapshot/:snapshotId/costed-bom-xlsx': endpoint({
        description: 'Download a costed BOM as an Excel file',
        pathParams: z.object({
            rfqId: z.string(),
            snapshotId: z.string(),
        }),
        queryParams: z.object({
            template_id: z.string().optional(),
        }),
        requestBody: z.undefined(),
        responseBody: z.object({ fileName: z.string(), blob: z.instanceof(Blob) }),
        handleResponse: async (response, endpoint) => {
            await assertSuccessfulResponseGenericErrorCode(response, endpoint);
            return { fileName: getNormalizedFileName(response), blob: await response.blob() };
        },
    }),

    'POST /sourcing/export/cost-breakdown-xlsx': endpoint({
        description: 'Returns an excel file with the assembly cost breakdown for one or more sourcing scenarios',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({ sourcing_scenario_ids: z.array(z.string()) }),
        responseBody: z.object({ fileName: z.string(), blob: z.instanceof(Blob) }),
        handleResponse: async (response, endpoint) => {
            await assertSuccessfulResponseGenericErrorCode(response, endpoint);
            return { fileName: getNormalizedFileName(response), blob: await response.blob() };
        },
    }),

    'GET /sourcing/export/supplier-award-files': endpoint({
        description: 'Returns a zip with the supplier award files for a demand group',
        pathParams: z.undefined(),
        queryParams: z.object({ demand_group_id: z.string() }),
        requestBody: z.undefined(),
        responseBody: z.object({ fileName: z.string(), blob: z.instanceof(Blob) }),
        handleResponse: async (response, endpoint) => {
            await assertSuccessfulResponseGenericErrorCode(response, endpoint);
            return { fileName: getNormalizedFileName(response), blob: await response.blob() };
        },
    }),
};
